import {
  Button,
  Calendar as AriaCalendar,
  CalendarCell,
  CalendarGrid,
  Heading,
  DateValue,
} from 'react-aria-components';

import { parseDate, getLocalTimeZone } from '@internationalized/date';
import { subMonths, addMonths } from 'date-fns';

type AttendanceData = {
  date: string;
  count: number;
}[];

type Props = {
  selectedDate: DateValue;
  setSelectedDate: (date: DateValue) => void;
  attendanceData: AttendanceData;
  currentMonth: Date;
  setCurrentMonth: (date: Date) => void;
};

const MonthlyCalendar = ({
  selectedDate,
  setSelectedDate,
  attendanceData,
  currentMonth,
  setCurrentMonth,
}: Props) => {
  const renderCell = (date: DateValue) => {
    const calendarDate = parseDate(date.toString());
    const attendanceItem = attendanceData.find((item) => item.date === calendarDate.toString());

    return (
      <CalendarCell
        date={calendarDate}
        className={`relative h-24 border-b border-r border-gray-200 bg-white p-2 first:border-l last:border-r`}
      >
        {({ formattedDate, isSelected, isOutsideMonth }) => (
          <div
            className={`absolute inset-0 ${isSelected ? 'bg-blue-100' : ''} ${isOutsideMonth ? 'opacity-50' : ''}`}
          >
            <span
              className={`text-s block p-2 ${isOutsideMonth ? 'text-gray-400' : ''} ${date.toDate(getLocalTimeZone()).getDay() === 0 ? 'text-red-600' : ''} ${date.toDate(getLocalTimeZone()).getDay() === 6 ? 'text-blue-600' : ''}`}
            >
              {formattedDate}
            </span>
            {attendanceItem && (
              <div className="mt-1 flex items-center">
                <HumanIcon />
                <span className="ml-1 text-xs text-gray-600">{attendanceItem.count}</span>
              </div>
            )}
          </div>
        )}
      </CalendarCell>
    );
  };
  return (
    <div className="w-full">
      <AriaCalendar
        aria-label="カレンダー"
        value={selectedDate}
        onChange={setSelectedDate}
        className="w-full"
      >
        <header className="mb-4 flex items-center justify-between">
          <Button
            slot="previous"
            className="rounded-full p-2 hover:bg-gray-100"
            onPress={() => {
              setCurrentMonth(subMonths(currentMonth, 1));
            }}
          >
            <PreviousIcon />
          </Button>
          <Heading className="text-lg font-semibold" />
          <Button
            slot="next"
            className="rounded-full p-2 hover:bg-gray-100"
            onPress={() => {
              setCurrentMonth(addMonths(currentMonth, 1));
            }}
          >
            <NextIcon />
          </Button>
        </header>
        <CalendarGrid className="w-full border border-gray-200">{renderCell}</CalendarGrid>
      </AriaCalendar>
    </div>
  );
};

const PreviousIcon = () => {
  return (
    <svg
      width="49"
      height="49"
      viewBox="0 0 49 49"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="1"
        y="1"
        width="47"
        height="47"
        rx="23.5"
        fill="#E8F1FE"
      />
      <rect
        x="1"
        y="1"
        width="47"
        height="47"
        rx="23.5"
        stroke="#0017C1"
      />
      <path
        d="M27.83 31.5L28.5 30.83L22.17 24.5L28.5 18.17L27.83 17.5L20.83 24.5L27.83 31.5Z"
        fill="#0017C1"
      />
    </svg>
  );
};

const NextIcon = () => {
  return (
    <svg
      width="49"
      height="49"
      viewBox="0 0 49 49"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0.5"
        y="-0.5"
        width="47"
        height="47"
        rx="23.5"
        transform="matrix(1 0 0 -1 0.5 47.5)"
        fill="#E8F1FE"
      />
      <rect
        x="0.5"
        y="-0.5"
        width="47"
        height="47"
        rx="23.5"
        transform="matrix(1 0 0 -1 0.5 47.5)"
        stroke="#0017C1"
      />
      <path
        d="M21.17 17.5L20.5 18.17L26.83 24.5L20.5 30.83L21.17 31.5L28.17 24.5L21.17 17.5Z"
        fill="#0017C1"
      />
    </svg>
  );
};

const HumanIcon = () => {
  return (
    <svg
      width="12"
      height="21"
      viewBox="0 0 12 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.99961 10.2846C8.70187 10.2846 10.8922 8.09445 10.8922 5.39234C10.8922 2.69031 8.70187 0.5 5.99961 0.5C3.29773 0.5 1.10742 2.69031 1.10742 5.39234C1.10742 8.09445 3.29773 10.2846 5.99961 10.2846Z"
        fill="#00118F"
      />
      <path
        d="M8.47258 11.7903H5.99938H3.52617C1.91332 12.7581 0.515625 15.0161 0.515625 16.9516C0.515625 18.1342 0.515625 20.4999 0.515625 20.4999H5.99941H11.4833C11.4833 20.4999 11.4833 18.1342 11.4833 16.9516C11.4832 15.0161 10.0855 12.7581 8.47258 11.7903Z"
        fill="#00118F"
      />
    </svg>
  );
};

export default MonthlyCalendar;
